import React, { useState, useEffect, useRef, useMemo } from 'react';
import Alert from '../../components/Alert/Alert';
import { usePersistedFilters } from '../../hooks/usePersistedFilters';
import { Star, ChartLine, FileText, Funnel, CircleDollarSign } from 'lucide-react';
import SalesAchievement from './SalesAchievement';
import SalesByServiceItem from './SalesByServiceItem';
import TotalRevenue from './TotalRevenue';
import ContractWon from './ContractWon';
import CustomerBalance from './CustomerBalance';
import SalesListDetail from './SalesListDetail';
import {
    fetchSalesAchievementData,
    fetchSalesByServiceItemData,
    fetchTotalRevenueData,
    fetchContractWonData,
    fetchCustomerBalanceData,
    fetchSalesListDetailData,
    fetchFilterOptions
} from './api/salesReportApi';
import {
    exportSalesAchievement,
    exportSalesByServiceItem,
    exportCustomerBalance,
    exportSalesListDetail
} from './utils/exportUtils';
import { createFilterHandler } from './utils/filterUtils';
import { PERIOD_OPTIONS, PAYMENT_OPTIONS, STATUS_OPTIONS } from './constants/filterOptions';
import FilterOffcanvas from './components/Filters/FilterOffcanvas';
import FilterAwarenessBar from '../../components/common/AdvancedFilter/FilterAwarenessBar';
import { useAuth } from '../../contexts/AuthProvider';

const SalesReport = () => {
    // Use global auth context with capabilities
    const { user, can, capabilitiesLoading } = useAuth();
    const { loadPersistedState, persistState: _persistState } = usePersistedFilters('sales_report');

    // Restore persisted state — runs once before first render via lazy initialisers
    const _persisted = (() => {
        try { return loadPersistedState()?.filters ?? null; } catch { return null; }
    })();

    const persistState = (state) => _persistState({ filters: state });

    const [activeTab, setActiveTab] = useState(_persisted?.activeTab ?? 'sales-achievement');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchQueryTotalRevenue, setSearchQueryTotalRevenue] = useState('');
    const [searchQueryTotalContractWon, setSearchQueryTotalContractWon] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [alert, setAlert] = useState(null);

    // Sales Achievement Data
    const [salesData, setSalesData] = useState([]);
    const [metrics, setMetrics] = useState({
        totalRevenue: 0,
        achievement: 0,
        invoicesPaid: 0,
        outstandingAR: 0,
        paidInvoiceCount: 0,
        outstandingInvoiceCount: 0
    });

    // Sales by Service Item Data
    const [salesByServiceData, setSalesByServiceData] = useState([]);
    const [salesByServiceMetrics, setSalesByServiceMetrics] = useState({
        totalMandays: 0,
        invoiceCount: 0,
        avgRateManday: 0,
        rateCertification: 0,
        mandaysCertification: 0,
        rateITAudit: 0,
        mandaysITAudit: 0,
        rateSustainability: 0,
        mandaysSustainability: 0,
        rateTraining: 0,
        mandaysTraining: 0
    });
    const [searchQueryServiceItem, setSearchQueryServiceItem] = useState('');
    
    // Sales by Service Item filter state (actual filters that trigger data fetch)
    const [salesByServiceFilters, setSalesByServiceFilters] = useState(() => ({
        period: 'this_year',
        salesCreator: [],
        serviceCategory: [],
        productCategory: [],
        standard: [],
        branchId: '',
        taggingIds: [],
        departmentId: [],
        serviceType: 'all',
        ...(_persisted?.salesByServiceFilters ?? {})
    }));

    // Sales by Service Item temporary filter state (for UI editing)
    const [tempSalesByServiceFilters, setTempSalesByServiceFilters] = useState(() => ({
        period: 'this_year',
        salesCreator: [],
        serviceCategory: [],
        productCategory: [],
        standard: [],
        branchId: '',
        taggingIds: [],
        departmentId: [],
        serviceType: 'all',
        ...(_persisted?.salesByServiceFilters ?? {})
    }));

    // Customer Balance Data
    const [customerBalanceData, setCustomerBalanceData] = useState([]);
    const [searchQueryCustomerBalance, setSearchQueryCustomerBalance] = useState('');

    // Sales List Detail Data
    const [salesListDetailData, setSalesListDetailData] = useState([]);
    const [searchQuerySalesListDetail, setSearchQuerySalesListDetail] = useState('');

    // Filter state (actual filters that trigger data fetch)
    const [filters, setFilters] = useState(() => ({
        period: 'this_year',
        creator: [],
        service: [],
        productCategory: [],
        standard: [],
        branchId: '',
        taggingIds: [],
        departmentId: [],
        ...(_persisted?.filters ?? {})
    }));

    // Temporary filter state (for UI editing)
    const [tempFilters, setTempFilters] = useState(() => ({
        period: 'this_year',
        creator: [],
        service: [],
        productCategory: [],
        standard: [],
        branchId: '',
        taggingIds: [],
        departmentId: [],
        ...(_persisted?.filters ?? {})
    }));

    // Customer Balance filter state (actual filters that trigger data fetch)
    const [customerBalanceFilters, setCustomerBalanceFilters] = useState(() => ({
        period: 'this_year',
        salesCreator: [],
        serviceCategory: [],
        productCategory: [],
        standard: [],
        payment: [],
        status: [],
        branchId: '',
        taggingIds: [],
        departmentId: [],
        ...(_persisted?.customerBalanceFilters ?? {})
    }));

    // Customer Balance temporary filter state (for UI editing)
    const [tempCustomerBalanceFilters, setTempCustomerBalanceFilters] = useState(() => ({
        period: 'this_year',
        salesCreator: [],
        serviceCategory: [],
        productCategory: [],
        standard: [],
        payment: [],
        status: [],
        branchId: '',
        taggingIds: [],
        departmentId: [],
        ...(_persisted?.customerBalanceFilters ?? {})
    }));

    // Sales List Detail filter state (actual filters that trigger data fetch)
    const [salesListDetailFilters, setSalesListDetailFilters] = useState(() => ({
        period: 'this_year',
        salesCreator: [],
        serviceCategory: [],
        productCategory: [],
        standard: [],
        payment: [],
        branchId: '',
        taggingIds: [],
        departmentId: [],
        ...(_persisted?.salesListDetailFilters ?? {})
    }));

    // Sales List Detail temporary filter state (for UI editing)
    const [tempSalesListDetailFilters, setTempSalesListDetailFilters] = useState(() => ({
        period: 'this_year',
        salesCreator: [],
        serviceCategory: [],
        productCategory: [],
        standard: [],
        payment: [],
        branchId: '',
        taggingIds: [],
        departmentId: [],
        ...(_persisted?.salesListDetailFilters ?? {})
    }));
    
    // Filter options state
    const [periodOptions, setPeriodOptions] = useState([]);
    const [userOptions, setUserOptions] = useState([]);
    const [serviceOptions, setServiceOptions] = useState([]);
    const [productCategoryOptions, setProductCategoryOptions] = useState([]);
    const [standardOptions, setStandardOptions] = useState([]);
    const [paymentOptions, setPaymentOptions] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);
    const [serviceItemOptions, setServiceItemOptions] = useState([]);
    const [departmentOptions, setDepartmentOptions] = useState([]);
    const [branchOptions, setBranchOptions] = useState([]);
    const [taggingOptions, setTaggingOptions] = useState([]);
    // Keep salesCreatorOptions for backward compatibility with Customer Balance and Sales List Detail tabs
    const [salesCreatorOptions, setSalesCreatorOptions] = useState([]);

    // Total Revenue tab — summary cards from /sales-report/total-revenue
    const [totalRevenueSummary, setTotalRevenueSummary] = useState(null);

    // Custom date range state
    const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
    const [customDateRange, setCustomDateRange] = useState({
        startDate: '',
        endDate: ''
    });
    const [dateRangeError, setDateRangeError] = useState('');

    // Refs to store latest filter values for offcanvas sync
    const filtersRef = useRef(filters);
    const customerBalanceFiltersRef = useRef(customerBalanceFilters);
    const salesListDetailFiltersRef = useRef(salesListDetailFilters);
    const salesByServiceFiltersRef = useRef(salesByServiceFilters);
    const activeTabRef = useRef(activeTab);
    const offcanvasInstanceRef = useRef(null);

    // Update refs when values change
    useEffect(() => {
        filtersRef.current = filters;
    }, [filters]);
    useEffect(() => {
        customerBalanceFiltersRef.current = customerBalanceFilters;
    }, [customerBalanceFilters]);
    useEffect(() => {
        salesListDetailFiltersRef.current = salesListDetailFilters;
    }, [salesListDetailFilters]);
    useEffect(() => {
        salesByServiceFiltersRef.current = salesByServiceFilters;
    }, [salesByServiceFilters]);
    useEffect(() => {
        activeTabRef.current = activeTab;
        persistState({
            activeTab,
            filters: filtersRef.current,
            customerBalanceFilters: customerBalanceFiltersRef.current,
            salesListDetailFilters: salesListDetailFiltersRef.current,
            salesByServiceFilters: salesByServiceFiltersRef.current,
        });
    }, [activeTab]);

    const getSharedFilterSnapshot = (sourceFilters = {}) => ({
        period: sourceFilters.period || 'this_year',
        branchId: sourceFilters.branchId || '',
        taggingIds: Array.isArray(sourceFilters.taggingIds) ? sourceFilters.taggingIds : [],
        departmentId: Array.isArray(sourceFilters.departmentId) ? sourceFilters.departmentId : [],
        startDate: sourceFilters.startDate || '',
        endDate: sourceFilters.endDate || '',
        creatorIds: Array.isArray(sourceFilters.creator) ? sourceFilters.creator : [],
        salesCreatorIds: Array.isArray(sourceFilters.salesCreator) ? sourceFilters.salesCreator : []
    });

    const applySharedFiltersToAchievement = (baseFilters, sharedFilters) => ({
        ...baseFilters,
        period: sharedFilters.period,
        branchId: sharedFilters.branchId,
        taggingIds: [...sharedFilters.taggingIds],
        departmentId: [...sharedFilters.departmentId],
        startDate: sharedFilters.startDate,
        endDate: sharedFilters.endDate,
        creator: [...sharedFilters.creatorIds]
    });

    const applySharedFiltersToServiceTabs = (baseFilters, sharedFilters) => ({
        ...baseFilters,
        period: sharedFilters.period,
        branchId: sharedFilters.branchId,
        taggingIds: [...sharedFilters.taggingIds],
        departmentId: [...sharedFilters.departmentId],
        startDate: sharedFilters.startDate,
        endDate: sharedFilters.endDate,
        salesCreator: sharedFilters.salesCreatorIds?.length > 0 ? [...sharedFilters.salesCreatorIds] : [...sharedFilters.creatorIds]
    });

    const syncSharedFiltersAcrossTabs = (sourceFilters) => {
        const sharedFilters = getSharedFilterSnapshot(sourceFilters);

        setFilters(prev => applySharedFiltersToAchievement(prev, sharedFilters));
        setTempFilters(prev => applySharedFiltersToAchievement(prev, sharedFilters));

        setSalesByServiceFilters(prev => applySharedFiltersToServiceTabs(prev, sharedFilters));
        setTempSalesByServiceFilters(prev => applySharedFiltersToServiceTabs(prev, sharedFilters));

        setCustomerBalanceFilters(prev => applySharedFiltersToServiceTabs(prev, sharedFilters));
        setTempCustomerBalanceFilters(prev => applySharedFiltersToServiceTabs(prev, sharedFilters));

        setSalesListDetailFilters(prev => applySharedFiltersToServiceTabs(prev, sharedFilters));
        setTempSalesListDetailFilters(prev => applySharedFiltersToServiceTabs(prev, sharedFilters));
    };

    const showAlert = (type, message) => {
        setAlert({ type, message });
        setTimeout(() => setAlert(null), 5000);
    };

    // Load filter options
    useEffect(() => {
        const loadFilterOptions = async () => {
            try {
                // Load static filter options from constants
                setPeriodOptions(PERIOD_OPTIONS);
                setPaymentOptions(PAYMENT_OPTIONS);
                setStatusOptions(STATUS_OPTIONS);

                // Load dynamic filter options from API with permission-based filtering
                const filterOptions = await fetchFilterOptions({
                    canViewGlobal: can('view_global', 'sales-report'),
                    canViewDepartment: can('view_department', 'sales-report'),
                    canViewOwn: can('view_own', 'sales-report'),
                    user: user
                });

                // Apply additional permission-based filtering for user options
                let userOptions = filterOptions.users || [];
                if (!can('view_global', 'sales-report')) {
                    if (can('view_department', 'sales-report') && user?.departmentId) {
                        // For department users, only show users from their department
                        userOptions = userOptions.filter(userOption =>
                            userOption.departmentId === user.departmentId
                        );
                    } else if (can('view_own', 'sales-report')) {
                        // For own users, only show themselves
                        userOptions = userOptions.filter(userOption =>
                            userOption.id === user.id
                        );
                    }
                }

                setUserOptions(userOptions);
                setServiceOptions(filterOptions.services || []);
                setProductCategoryOptions(filterOptions.productCategories || []);
                setStandardOptions(filterOptions.standards || []);
                setDepartmentOptions(filterOptions.departments || []);
                setBranchOptions(filterOptions.branches || []);
                setTaggingOptions(filterOptions.taggingOptions || []);
                // Keep serviceItemOptions for Sales List Detail tab
                setServiceItemOptions(filterOptions.serviceItems || []);
                // Set salesCreatorOptions from filtered userOptions for backward compatibility (filter out empty options)
                const filteredUsers = userOptions.filter(user =>
                    user.id != null && user.id !== '' && user.name && user.name.trim() !== ''
                );
                setSalesCreatorOptions(filteredUsers);
                } catch (error) {
                setUserOptions([]);
                setServiceOptions([]);
                setProductCategoryOptions([]);
                    setStandardOptions([]);
                    setServiceItemOptions([]);
                setBranchOptions([]);
                setTaggingOptions([]);
                setSalesCreatorOptions([]);
            }
        };

        loadFilterOptions();
    }, [can, user]);

    const cascadedSalesAchievementCreatorOptions = useMemo(() => {
        const selectedDepts = (tempFilters.departmentId || []).map(String).filter(Boolean);
        const base = userOptions.map(u => ({
            ...u,
            name: String(u.status) === '0' ? `${u.name} (Inactive)` : u.name
        }));
        if (!selectedDepts.length) return base;
        return base.filter(u => u.departmentId != null && selectedDepts.includes(String(u.departmentId)));
    }, [tempFilters.departmentId, userOptions]);

    const cascadedSalesCreatorOptions = useMemo(() => {
        return salesCreatorOptions.map(u => ({
            ...u,
            name: String(u.status) === '0' ? `${u.name} (Inactive)` : u.name
        }));
    }, [salesCreatorOptions]);

    // Sync temporary filters with actual filters when tab changes
    useEffect(() => {
        if (activeTab === 'customer-balance') {
            // Ensure period defaults to 'this_year' if empty
            const customerBalanceFiltersCopy = { ...customerBalanceFilters };
            if (!customerBalanceFiltersCopy.period) {
                customerBalanceFiltersCopy.period = 'this_year';
            }
            setTempCustomerBalanceFilters(customerBalanceFiltersCopy);
        } else if (activeTab === 'sales-list-detail') {
            // Ensure period defaults to 'this_year' if empty
            const salesListDetailFiltersCopy = { ...salesListDetailFilters };
            if (!salesListDetailFiltersCopy.period) {
                salesListDetailFiltersCopy.period = 'this_year';
            }
            setTempSalesListDetailFilters(salesListDetailFiltersCopy);
        } else if (activeTab === 'sales-by-service') {
            const salesByServiceFiltersCopy = { ...salesByServiceFilters };
            if (!salesByServiceFiltersCopy.serviceType) {
                salesByServiceFiltersCopy.serviceType = 'all';
            }
            if (!salesByServiceFiltersCopy.period) {
                salesByServiceFiltersCopy.period = 'this_year';
            }
            setTempSalesByServiceFilters(salesByServiceFiltersCopy);
        } else {
            // Sales Achievement: ensure period defaults to 'this_year' if empty
            const salesAchievementFilters = { ...filters };
            if (!salesAchievementFilters.period) {
                salesAchievementFilters.period = 'this_year';
            }
            setTempFilters(salesAchievementFilters);
        }
    }, [activeTab]);

    // Sync temporary filters when offcanvas is shown
    useEffect(() => {
        const offcanvasElement = document.getElementById('offcanvasEnd');
        if (!offcanvasElement || !window.bootstrap?.Offcanvas) return;

        offcanvasInstanceRef.current = window.bootstrap.Offcanvas.getOrCreateInstance(offcanvasElement, {
            backdrop: true,
            keyboard: true,
            scroll: false
        });

        const handleShow = () => {
            // Sync temporary filters with actual filters when offcanvas opens
            // Use refs to get the latest values without adding them to dependencies
            const currentTab = activeTabRef.current;
            if (currentTab === 'customer-balance') {
                const customerBalanceFiltersCopy = { ...customerBalanceFiltersRef.current };
                if (!customerBalanceFiltersCopy.period) {
                    customerBalanceFiltersCopy.period = 'this_year';
                }
                // Restore custom date range if period is custom
                if (customerBalanceFiltersCopy.period === 'custom' && customerBalanceFiltersCopy.startDate && customerBalanceFiltersCopy.endDate) {
                    setCustomDateRange({
                        startDate: customerBalanceFiltersCopy.startDate,
                        endDate: customerBalanceFiltersCopy.endDate
                    });
                }
                setTempCustomerBalanceFilters(customerBalanceFiltersCopy);
            } else if (currentTab === 'sales-list-detail') {
                const salesListDetailFiltersCopy = { ...salesListDetailFiltersRef.current };
                if (!salesListDetailFiltersCopy.period) {
                    salesListDetailFiltersCopy.period = 'this_year';
                }
                // Restore custom date range if period is custom
                if (salesListDetailFiltersCopy.period === 'custom' && salesListDetailFiltersCopy.startDate && salesListDetailFiltersCopy.endDate) {
                    setCustomDateRange({
                        startDate: salesListDetailFiltersCopy.startDate,
                        endDate: salesListDetailFiltersCopy.endDate
                    });
                }
                setTempSalesListDetailFilters(salesListDetailFiltersCopy);
            } else if (currentTab === 'sales-by-service') {
                const salesByServiceFiltersCopy = { ...salesByServiceFiltersRef.current };
                if (!salesByServiceFiltersCopy.serviceType) {
                    salesByServiceFiltersCopy.serviceType = 'all';
                }
                if (!salesByServiceFiltersCopy.period) {
                    salesByServiceFiltersCopy.period = 'this_year';
                }
                if (salesByServiceFiltersCopy.period === 'custom' && salesByServiceFiltersCopy.startDate && salesByServiceFiltersCopy.endDate) {
                    setCustomDateRange({
                        startDate: salesByServiceFiltersCopy.startDate,
                        endDate: salesByServiceFiltersCopy.endDate
                    });
                }
                setTempSalesByServiceFilters(salesByServiceFiltersCopy);
            } else {
                // Sales Achievement + Total Revenue
                const salesAchievementFilters = { ...filtersRef.current };
                if (!salesAchievementFilters.period) {
                    salesAchievementFilters.period = 'this_year';
                }
                // Restore custom date range if period is custom
                if (salesAchievementFilters.period === 'custom' && salesAchievementFilters.startDate && salesAchievementFilters.endDate) {
                    setCustomDateRange({
                        startDate: salesAchievementFilters.startDate,
                        endDate: salesAchievementFilters.endDate
                    });
                }
                setTempFilters(salesAchievementFilters);
            }
        };

        const handleHidden = () => {
            // Keep cleanup minimal to avoid breaking Bootstrap backdrop re-creation on next open.
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
        };

        offcanvasElement.addEventListener('show.bs.offcanvas', handleShow);
        offcanvasElement.addEventListener('hidden.bs.offcanvas', handleHidden);
        return () => {
            offcanvasElement.removeEventListener('show.bs.offcanvas', handleShow);
            offcanvasElement.removeEventListener('hidden.bs.offcanvas', handleHidden);
            offcanvasInstanceRef.current?.dispose();
            offcanvasInstanceRef.current = null;
        };
    }, []); // Empty dependency array - only set up listener once

    // Enhanced filter change handlers that detect custom range selection
    const handleFilterChange = (name, value) => {
        setTempFilters(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Show custom date picker when custom range is selected
        if (name === 'period' && value === 'custom') {
            setShowCustomDatePicker(true);
        } else if (name === 'period' && value !== 'custom') {
            // Clear custom date range when switching away from custom
            setCustomDateRange({ startDate: '', endDate: '' });
            setDateRangeError('');
        }
    };

    const handleCustomerBalanceFilterChange = (name, value) => {
        setTempCustomerBalanceFilters(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Show custom date picker when custom range is selected
        if (name === 'period' && value === 'custom') {
            setShowCustomDatePicker(true);
        } else if (name === 'period' && value !== 'custom') {
            // Clear custom date range when switching away from custom
            setCustomDateRange({ startDate: '', endDate: '' });
            setDateRangeError('');
        }
    };

    const handleSalesListDetailFilterChange = (name, value) => {
        setTempSalesListDetailFilters(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Show custom date picker when custom range is selected
        if (name === 'period' && value === 'custom') {
            setShowCustomDatePicker(true);
        } else if (name === 'period' && value !== 'custom') {
            // Clear custom date range when switching away from custom
            setCustomDateRange({ startDate: '', endDate: '' });
            setDateRangeError('');
        }
    };

    const handleSalesByServiceFilterChange = (name, value) => {
        setTempSalesByServiceFilters(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Show custom date picker when custom range is selected
        if (name === 'period' && value === 'custom') {
            setShowCustomDatePicker(true);
        } else if (name === 'period' && value !== 'custom') {
            // Clear custom date range when switching away from custom
            setCustomDateRange({ startDate: '', endDate: '' });
            setDateRangeError('');
        }
    };

    // Custom date range handlers
    const handleCustomDateChange = (dateType, value) => {
        setCustomDateRange(prev => {
            const newRange = {
                ...prev,
                [dateType]: value
            };
            
            // Clear any previous error
            setDateRangeError('');
            
            // Validate that start date is not later than end date
            if (newRange.startDate && newRange.endDate) {
                const startDate = new Date(newRange.startDate);
                const endDate = new Date(newRange.endDate);
                
                if (startDate > endDate) {
                    setDateRangeError('Start date cannot be later than end date');
                    // If start date is later than end date, clear the end date
                    if (dateType === 'startDate') {
                        newRange.endDate = '';
                    } else {
                        newRange.startDate = '';
                    }
                }
            }
            
            return newRange;
        });
    };

    const handleApplyCustomDateRange = () => {
        if (customDateRange.startDate && customDateRange.endDate) {
            // Final validation before applying
            const startDate = new Date(customDateRange.startDate);
            const endDate = new Date(customDateRange.endDate);
            
            if (startDate > endDate) {
                setDateRangeError('Start date cannot be later than end date');
                return;
            }
            
            // Update the appropriate temporary filter with custom date range
            if (activeTab === 'customer-balance') {
                setTempCustomerBalanceFilters(prev => ({
                    ...prev,
                    period: 'custom',
                    startDate: customDateRange.startDate,
                    endDate: customDateRange.endDate
                }));
            } else if (activeTab === 'sales-list-detail') {
                setTempSalesListDetailFilters(prev => ({
                    ...prev,
                    period: 'custom',
                    startDate: customDateRange.startDate,
                    endDate: customDateRange.endDate
                }));
            } else if (activeTab === 'sales-by-service') {
                setTempSalesByServiceFilters(prev => ({
                    ...prev,
                    period: 'custom',
                    startDate: customDateRange.startDate,
                    endDate: customDateRange.endDate
                }));
            } else {
                setTempFilters(prev => ({
                    ...prev,
                    period: 'custom',
                    startDate: customDateRange.startDate,
                    endDate: customDateRange.endDate
                }));
            }
            
            setShowCustomDatePicker(false);
            setDateRangeError('');
        }
    };

    const handleCancelCustomDateRange = () => {
        setShowCustomDatePicker(false);
        setCustomDateRange({
            startDate: '',
            endDate: ''
        });
        setDateRangeError('');
        
        // Reset period to previous value if it was set to custom
        if (activeTab === 'customer-balance') {
            const currentPeriod = tempCustomerBalanceFilters.period;
            if (currentPeriod === 'custom') {
                setTempCustomerBalanceFilters(prev => ({
                    ...prev,
                    period: 'this_year'
                }));
            }
        } else if (activeTab === 'sales-list-detail') {
            const currentPeriod = tempSalesListDetailFilters.period;
            if (currentPeriod === 'custom') {
                setTempSalesListDetailFilters(prev => ({
                    ...prev,
                    period: 'this_year'
                }));
            }
        } else if (activeTab === 'sales-by-service') {
            const currentPeriod = tempSalesByServiceFilters.period;
            if (currentPeriod === 'custom') {
                setTempSalesByServiceFilters(prev => ({
                    ...prev,
                    period: 'this_year'
                }));
            }
        } else {
            const currentPeriod = tempFilters.period;
            if (currentPeriod === 'custom') {
                setTempFilters(prev => ({
                    ...prev,
                    period: 'this_year'
                }));
            }
        }
    };

    // Handle filter reset (reset both temporary and actual filters)
    const handleResetFilter = () => {
        // Reset custom date range
        setCustomDateRange({ startDate: '', endDate: '' });
        setDateRangeError('');
        setShowCustomDatePicker(false);

        const resetSharedFilters = {
            period: 'this_year',
            branchId: '',
            taggingIds: [],
            departmentId: [],
            startDate: '',
            endDate: '',
            creatorIds: [],
            salesCreatorIds: []
        };
        
        if (activeTab === 'customer-balance') {
            const resetFilters = {
                period: 'this_year', // Default to this year
                salesCreator: '',
                serviceCategory: '',
                productCategory: '',
                standard: '',
                payment: '',
                status: '',
                branchId: '',
                taggingIds: [],
                departmentId: [],
                startDate: '',
                endDate: ''
            };
            setTempCustomerBalanceFilters(resetFilters);
            setCustomerBalanceFilters(resetFilters);
            setFilters(prev => applySharedFiltersToAchievement(prev, resetSharedFilters));
            setTempFilters(prev => applySharedFiltersToAchievement(prev, resetSharedFilters));
            setSalesByServiceFilters(prev => applySharedFiltersToServiceTabs(prev, resetSharedFilters));
            setTempSalesByServiceFilters(prev => applySharedFiltersToServiceTabs(prev, resetSharedFilters));
            setSalesListDetailFilters(prev => applySharedFiltersToServiceTabs(prev, resetSharedFilters));
            setTempSalesListDetailFilters(prev => applySharedFiltersToServiceTabs(prev, resetSharedFilters));
        } else if (activeTab === 'sales-list-detail') {
            const resetFilters = {
                period: 'this_year', // Default to this year
                salesCreator: '',
                serviceCategory: '',
                productCategory: '',
                standard: '',
                payment: '',
                branchId: '',
                taggingIds: [],
                departmentId: [],
                startDate: '',
                endDate: ''
            };
            setTempSalesListDetailFilters(resetFilters);
            setSalesListDetailFilters(resetFilters);
            setFilters(prev => applySharedFiltersToAchievement(prev, resetSharedFilters));
            setTempFilters(prev => applySharedFiltersToAchievement(prev, resetSharedFilters));
            setSalesByServiceFilters(prev => applySharedFiltersToServiceTabs(prev, resetSharedFilters));
            setTempSalesByServiceFilters(prev => applySharedFiltersToServiceTabs(prev, resetSharedFilters));
            setCustomerBalanceFilters(prev => applySharedFiltersToServiceTabs(prev, resetSharedFilters));
            setTempCustomerBalanceFilters(prev => applySharedFiltersToServiceTabs(prev, resetSharedFilters));
        } else if (activeTab === 'sales-by-service') {
            const resetFilters = {
                period: 'this_year', // Default to this year
                salesCreator: '',
                serviceCategory: '',
                productCategory: '',
                standard: '',
                serviceType: 'all', // Default to 'all'
                branchId: '',
                taggingIds: [],
                departmentId: [],
                startDate: '',
                endDate: ''
            };
            setTempSalesByServiceFilters(resetFilters);
            setSalesByServiceFilters(resetFilters);
            setFilters(prev => applySharedFiltersToAchievement(prev, resetSharedFilters));
            setTempFilters(prev => applySharedFiltersToAchievement(prev, resetSharedFilters));
            setCustomerBalanceFilters(prev => applySharedFiltersToServiceTabs(prev, resetSharedFilters));
            setTempCustomerBalanceFilters(prev => applySharedFiltersToServiceTabs(prev, resetSharedFilters));
            setSalesListDetailFilters(prev => applySharedFiltersToServiceTabs(prev, resetSharedFilters));
            setTempSalesListDetailFilters(prev => applySharedFiltersToServiceTabs(prev, resetSharedFilters));
        } else {
            // Sales Achievement + Total Revenue
            const resetFilters = {
                period: 'this_year',
                creator: '',
                service: '',
                productCategory: '',
                standard: '',
                branchId: '',
                taggingIds: [],
                startDate: '',
                endDate: ''
            };
            setTempFilters(resetFilters);
            setFilters(resetFilters);
            setSalesByServiceFilters(prev => applySharedFiltersToServiceTabs(prev, resetSharedFilters));
            setTempSalesByServiceFilters(prev => applySharedFiltersToServiceTabs(prev, resetSharedFilters));
            setCustomerBalanceFilters(prev => applySharedFiltersToServiceTabs(prev, resetSharedFilters));
            setTempCustomerBalanceFilters(prev => applySharedFiltersToServiceTabs(prev, resetSharedFilters));
            setSalesListDetailFilters(prev => applySharedFiltersToServiceTabs(prev, resetSharedFilters));
            setTempSalesListDetailFilters(prev => applySharedFiltersToServiceTabs(prev, resetSharedFilters));
        }
        persistState({
            activeTab,
            filters: filtersRef.current,
            customerBalanceFilters: customerBalanceFiltersRef.current,
            salesListDetailFilters: salesListDetailFiltersRef.current,
            salesByServiceFilters: salesByServiceFiltersRef.current,
        });

        // Close offcanvas
        const offcanvasElement = document.getElementById('offcanvasEnd');
        if (offcanvasElement) {
            const offcanvasInstance = window.bootstrap?.Offcanvas?.getInstance(offcanvasElement);
            if (offcanvasInstance) {
                offcanvasInstance.hide();
            }
        }
    };

    // Handle filter apply (copy temporary filters to actual filters)
    const handleApplyFilter = () => {
        let nextFilters = filters;
        let nextCustomerBalanceFilters = customerBalanceFilters;
        let nextSalesListDetailFilters = salesListDetailFilters;
        let nextSalesByServiceFilters = salesByServiceFilters;

        if (activeTab === 'customer-balance') {
            nextCustomerBalanceFilters = { ...tempCustomerBalanceFilters };
            setCustomerBalanceFilters(nextCustomerBalanceFilters);
            syncSharedFiltersAcrossTabs(tempCustomerBalanceFilters);
        } else if (activeTab === 'sales-list-detail') {
            nextSalesListDetailFilters = { ...tempSalesListDetailFilters };
            setSalesListDetailFilters(nextSalesListDetailFilters);
            syncSharedFiltersAcrossTabs(tempSalesListDetailFilters);
        } else if (activeTab === 'sales-by-service') {
            nextSalesByServiceFilters = { ...tempSalesByServiceFilters };
            setSalesByServiceFilters(nextSalesByServiceFilters);
            syncSharedFiltersAcrossTabs(tempSalesByServiceFilters);
        } else {
            // Sales Achievement + Total Revenue
            nextFilters = { ...tempFilters };
            setFilters(nextFilters);
            syncSharedFiltersAcrossTabs(tempFilters);
        }

        persistState({
            activeTab,
            filters: nextFilters,
            customerBalanceFilters: nextCustomerBalanceFilters,
            salesListDetailFilters: nextSalesListDetailFilters,
            salesByServiceFilters: nextSalesByServiceFilters,
        });

        // Close offcanvas
        const offcanvasElement = document.getElementById('offcanvasEnd');
        if (offcanvasElement) {
            const offcanvasInstance = window.bootstrap?.Offcanvas?.getInstance(offcanvasElement);
            if (offcanvasInstance) {
                offcanvasInstance.hide();
            }
        }
        // Data will be refetched automatically via useEffect when filters change
        showAlert('success', 'Filter applied successfully');
    };

    // Format currency without symbol
    const formatCurrencyValue = (value) => {
        return new Intl.NumberFormat('id-ID').format(value);
    };

    // Fetch data when tab changes or filters change
    useEffect(() => {
        if (capabilitiesLoading) return; // Wait for capabilities to load
        
        const fetchData = async () => {
        setIsLoading(true);
        try {
                // Build permission-based filters
                const permissionFilters = {};
                
                // ✅ Permission-based filtering hierarchy:
                // 1. view_global → see all (no filter)
                // 2. view_department → filter by user's department
                // 3. view_own → filter by user ID
                
                if (!can('view_global', 'sales-report')) {
                    if (can('view_department', 'sales-report')) {
                        // Filter by department
                        if (user?.departmentId) {
                            permissionFilters.viewDepartmentId = user.departmentId;
                        }
                    } else if (can('view_own', 'sales-report')) {
                        // Filter by user (created by)
                        if (user?.id) {
                            permissionFilters.viewOwnUserId = user.id;
                        }
                    }
                }
                
                if (activeTab === 'sales-achievement') {
                    const result = await fetchSalesAchievementData({ ...filters, ...permissionFilters });
                    setMetrics(result.metrics);
                    setSalesData(result.salesData);
                } else if (activeTab === 'total-revenue') {
                    const neutralServiceFilters = {
                        period: filters.period,
                        startDate: filters.startDate,
                        endDate: filters.endDate,
                        salesCreator: [],
                        serviceCategory: [],
                        standard: [],
                        departmentId: [],
                        serviceType: 'all',
                        ...permissionFilters
                    };
                    const result = await fetchTotalRevenueData(
                        { ...filters, ...permissionFilters },
                        neutralServiceFilters
                    );
                    const listMetrics = result.achievementMetrics || result.metrics || {};
                    setMetrics(listMetrics);
                    setTotalRevenueSummary(result.summary || null);
                    const rows = result.salesData || [];
                    setSalesData(
                        rows.map((row) => ({
                            ...row,
                            invoiceCount:
                                row.proposalCount != null && row.proposalCount !== ''
                                    ? Number(row.proposalCount)
                                    : Number(row.invoiceCount ?? 0)
                        }))
                    );
                } else if (activeTab === 'total-contract-won') {
                    const result = await fetchContractWonData({ ...filters, ...permissionFilters });
                    setMetrics(result.achievementMetrics || result.metrics || {});
                    setTotalRevenueSummary(result.summary || null);
                    const rows = result.salesData || [];
                    setSalesData(
                        rows.map((row) => ({
                            ...row,
                            invoiceCount: Number(row.proposalCount ?? row.invoiceCount ?? 0)
                        }))
                    );
                } else if (activeTab === 'sales-by-service') {
                    const result = await fetchSalesByServiceItemData({ ...salesByServiceFilters, ...permissionFilters });
                    setSalesByServiceData(result.data);
                    setSalesByServiceMetrics(result.metrics);
                } else if (activeTab === 'customer-balance') {
                    const [customerData, salesDetailData] = await Promise.all([
                        fetchCustomerBalanceData({ ...customerBalanceFilters, ...permissionFilters }),
                        fetchSalesListDetailData({ ...customerBalanceFilters, ...permissionFilters })
                    ]);
                    setCustomerBalanceData(customerData);
                    setSalesListDetailData(salesDetailData);
                } else if (activeTab === 'sales-list-detail') {
                    const data = await fetchSalesListDetailData({ ...salesListDetailFilters, ...permissionFilters });
                    setSalesListDetailData(data);
                }
                } catch (error) {
                showAlert('danger', `Failed to load data: ${error.message || 'Unknown error'}`);
                if (activeTab === 'sales-achievement') {
            setMetrics({
                        totalRevenue: 0,
                        achievement: 0,
                        invoicesPaid: 0,
                        outstandingAR: 0,
                        paidInvoiceCount: 0,
                        outstandingInvoiceCount: 0
                    });
                    setSalesData([]);
                } else if (activeTab === 'total-revenue') {
                    setTotalRevenueSummary(null);
                    setMetrics({
                        totalRevenue: 0,
                        achievement: 0,
                        invoicesPaid: 0,
                        outstandingAR: 0,
                        paidInvoiceCount: 0,
                        outstandingInvoiceCount: 0,
                        target: 0,
                        targetRevenue: 0,
                        targetPeriodLabel: '',
                        targetYear: null
                    });
                    setSalesData([]);
                } else if (activeTab === 'total-contract-won') {
                    setTotalRevenueSummary(null);
                    setMetrics({});
                    setSalesData([]);
                } else if (activeTab === 'sales-by-service') {
                    setSalesByServiceData([]);
                    setSalesByServiceMetrics({
                        totalMandays: 0,
                        invoiceCount: 0,
                        avgRateManday: 0,
                        rateCertification: 0,
                        mandaysCertification: 0,
                        rateITAudit: 0,
                        mandaysITAudit: 0,
                        rateSustainability: 0,
                        mandaysSustainability: 0,
                        rateTraining: 0,
                        mandaysTraining: 0
                    });
                } else if (activeTab === 'customer-balance') {
                    setCustomerBalanceData([]);
                } else if (activeTab === 'sales-list-detail') {
                    setSalesListDetailData([]);
                }
        } finally {
            setIsLoading(false);
        }
    };

        fetchData();
    }, [filters, customerBalanceFilters, salesListDetailFilters, salesByServiceFilters, activeTab, capabilitiesLoading, can, user]);

    // Filter data by search query for export
    const filteredData = salesData.filter(item => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return item.name?.toLowerCase().includes(query);
    });

    const filteredServiceItemData = salesByServiceData.filter(item => {
        if (!searchQueryServiceItem) return true;
        const query = searchQueryServiceItem.toLowerCase();
        return (
            item.serviceItem?.toLowerCase().includes(query) ||
            item.standard?.toLowerCase().includes(query)
        );
    });

    const filteredCustomerBalanceData = customerBalanceData.filter(item => {
        if (!searchQueryCustomerBalance) return true;
        const query = searchQueryCustomerBalance.toLowerCase();
        return (
            item.customerName?.toLowerCase().includes(query) ||
            item.status?.toLowerCase().includes(query)
        );
    });

    const filteredSalesListDetailData = salesListDetailData.filter(item => {
        if (!searchQuerySalesListDetail) return true;
        const query = searchQuerySalesListDetail.toLowerCase();
        return (
            item.invoiceId?.toLowerCase().includes(query) ||
            item.pelanggan?.toLowerCase().includes(query) ||
            item.serviceItem?.toLowerCase().includes(query) ||
            item.salesCreator?.toLowerCase().includes(query) ||
            item.statusBayar?.toLowerCase().includes(query)
        );
    });

    const filteredDataTotalRevenue = salesData.filter(item => {
        if (!searchQueryTotalRevenue) return true;
        const query = searchQueryTotalRevenue.toLowerCase();
        return item.name?.toLowerCase().includes(query);
    });

    // Export handlers using utility functions with permission check
    const handleExportExcel = () => {
        if (!can('export_data', 'sales-report')) {
            showAlert('danger', 'You do not have permission to export data.');
            return;
        }
        exportSalesAchievement(filteredData, showAlert);
    };

    const handleExportExcelTotalRevenue = () => {
        if (!can('export_data', 'sales-report')) {
            showAlert('danger', 'You do not have permission to export data.');
            return;
        }
        exportSalesAchievement(filteredDataTotalRevenue, showAlert);
    };
    
    const handleExportExcelServiceItem = () => {
        if (!can('export_data', 'sales-report')) {
            showAlert('danger', 'You do not have permission to export data.');
            return;
        }
        exportSalesByServiceItem(filteredServiceItemData, showAlert);
    };
    
    const handleExportExcelCustomerBalance = () => {
        if (!can('export_data', 'sales-report')) {
            showAlert('danger', 'You do not have permission to export data.');
            return;
        }
        exportCustomerBalance(filteredCustomerBalanceData, showAlert);
    };
    
    const handleExportExcelSalesListDetail = () => {
        if (!can('export_data', 'sales-report')) {
            showAlert('danger', 'You do not have permission to export data.');
            return;
        }
        exportSalesListDetail(filteredSalesListDetailData, showAlert);
    };

    const handleOpenFilter = () => {
        if (!window.bootstrap?.Offcanvas) return;
        const offcanvasElement = document.getElementById('offcanvasEnd');
        if (!offcanvasElement) return;
        const offcanvasInstance = window.bootstrap.Offcanvas.getOrCreateInstance(offcanvasElement, {
            backdrop: true,
            keyboard: true,
            scroll: false
        });
        offcanvasInstanceRef.current = offcanvasInstance;
        offcanvasInstance.show();
    };

    const buildFilterTags = (activeFilters = {}) => {
        const tags = [];
        const addArrayTag = (key, label, values) => {
            if (!Array.isArray(values) || values.length === 0) return;
            tags.push({ key, label: `${label}: ${values.length} selected` });
        };

        if (activeFilters.period && activeFilters.period !== 'all') {
            tags.push({ key: 'period', label: `Period: ${String(activeFilters.period).replace(/_/g, ' ')}` });
        }
        if (activeFilters.startDate || activeFilters.endDate) {
            tags.push({
                key: 'dateRange',
                label: `Date: ${[activeFilters.startDate, activeFilters.endDate].filter(Boolean).join(' - ')}`
            });
        }
        addArrayTag('creator', 'Creator', activeFilters.creator || activeFilters.salesCreator);
        addArrayTag('service', 'Service', activeFilters.service || activeFilters.serviceCategory);
        addArrayTag('productCategory', 'Product Category', activeFilters.productCategory);
        addArrayTag('standard', 'Standard', activeFilters.standard);
        addArrayTag('payment', 'Payment', activeFilters.payment);
        addArrayTag('status', 'Status', activeFilters.status);
        addArrayTag('departmentId', 'Department', activeFilters.departmentId);
        addArrayTag('taggingIds', 'Tagging', activeFilters.taggingIds);

        if (activeFilters.branchId) {
            tags.push({ key: 'branchId', label: `Branch: ${activeFilters.branchId}` });
        }
        if (activeFilters.serviceType && activeFilters.serviceType !== 'all') {
            tags.push({ key: 'serviceType', label: `Service Type: ${activeFilters.serviceType}` });
        }

        return tags;
    };

    const currentFilterTags = useMemo(() => {
        if (activeTab === 'sales-by-service') {
            return buildFilterTags(salesByServiceFilters);
        }
        if (activeTab === 'customer-balance') {
            return buildFilterTags(customerBalanceFilters);
        }
        if (activeTab === 'sales-list-detail') {
            return buildFilterTags(salesListDetailFilters);
        }
        return buildFilterTags(filters);
    }, [activeTab, filters, salesByServiceFilters, customerBalanceFilters, salesListDetailFilters]);

    const currentActiveCount = currentFilterTags.length;
    const currentTabTitle = {
        'sales-achievement': 'Sales Achievement',
        'total-revenue': 'Total Revenue',
        'sales-by-service': 'Sales by Service Item',
        'customer-balance': 'Customer Balance',
        'sales-list-detail': 'Sales List Detail',
        'total-contract-won': 'Contract Won',
    }[activeTab] || 'Sales Report';

    return (
        <div className="page">
            {alert && (
                <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />
            )}

            <div className="page-header">
                <div className="container-xl">
                    <div className="row g-2 align-items-center">
                        <div className="col">
                            <h2 className="page-title">Summary of Sales Achievement (Sales Achievement)</h2>
                        </div>
                        <div className="col-auto ms-auto">
                            <button
                                type="button"
                                className="btn"
                                onClick={handleOpenFilter}
                            >
                                <Funnel className="icon icon-tabler icon-tabler-download text-black" />
                                Filter
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body">
                <div className="container-xl">
                    {/* Navigation Tabs - Segmented Style */}
                    <nav className="nav nav-segmented mb-3 flex-wrap" role="tablist">
                        <button
                            className={`nav-link ${activeTab === 'sales-achievement' ? 'active' : ''}`}
                            role="tab"
                            onClick={() => setActiveTab('sales-achievement')}
                            aria-selected={activeTab === 'sales-achievement'}
                            aria-current={activeTab === 'sales-achievement' ? 'page' : undefined}
                            type="button"
                        >
                            <Star className="icon icon-tabler icon-tabler-download text-black" />
                            Sales Achievement
                        </button>
                        <button
                            className={`nav-link ${activeTab === 'total-revenue' ? 'active' : ''}`}
                            role="tab"
                            onClick={() => setActiveTab('total-revenue')}
                            aria-selected={activeTab === 'total-revenue'}
                            aria-current={activeTab === 'total-revenue' ? 'page' : undefined}
                            type="button"
                        >
                            <CircleDollarSign className="icon icon-tabler icon-tabler-download text-black" />
                            Total Revenue
                        </button>
                        <button
                            className={`nav-link ${activeTab === 'sales-by-service' ? 'active' : ''}`}
                            role="tab"
                            onClick={() => setActiveTab('sales-by-service')}
                            aria-selected={activeTab === 'sales-by-service'}
                            type="button"
                        >
                            <ChartLine className="icon icon-tabler icon-tabler-download text-black" />
                            Sales by Service Item
                        </button>
                        <button
                            className={`nav-link ${activeTab === 'customer-balance' ? 'active' : ''}`}
                            role="tab"
                            onClick={() => setActiveTab('customer-balance')}
                            aria-selected={activeTab === 'customer-balance'}
                            type="button"
                        >
                            <FileText className="icon icon-tabler icon-tabler-download text-black" />
                            Customer Balance
                        </button>
                        <button
                            className={`nav-link ${activeTab === 'sales-list-detail' ? 'active' : ''}`}
                            role="tab"
                            onClick={() => setActiveTab('sales-list-detail')}
                            aria-selected={activeTab === 'sales-list-detail'}
                            type="button"
                        >
                            <FileText className="icon icon-tabler icon-tabler-download text-black" />
                            Sales List Detail
                        </button>
                        <button
                            className={`nav-link ${activeTab === 'total-contract-won' ? 'active' : ''}`}
                            role="tab"
                            onClick={() => setActiveTab('total-contract-won')}
                            aria-selected={activeTab === 'total-contract-won'}
                            aria-current={activeTab === 'total-contract-won' ? 'page' : undefined}
                            type="button"
                        >
                            <FileText className="icon icon-tabler icon-tabler-download text-black" />
                            Contract Won
                        </button>
                    </nav>

                    <FilterAwarenessBar
                        title={`${currentTabTitle} is showing a filtered view`}
                        summary={`Filtered by ${currentActiveCount} ${currentActiveCount === 1 ? 'criterion' : 'criteria'}`}
                        detail={currentFilterTags.map((tag) => tag.label).join(' · ')}
                        loading={isLoading}
                        activeCount={currentActiveCount}
                        filterTags={currentFilterTags}
                        onRemoveFilter={null}
                        onResetFilters={null}
                    />

                    {/* Total Revenue Tab Content */}
                    {activeTab === 'total-revenue' && (
                        <TotalRevenue
                            summary={totalRevenueSummary}
                            salesAchievementData={salesData}
                            achievementMetrics={metrics}
                            filters={filters}
                            searchQuery={searchQueryTotalRevenue}
                            setSearchQuery={setSearchQueryTotalRevenue}
                            isLoading={isLoading}
                            handleExportExcel={can('export_data', 'sales-report') ? handleExportExcelTotalRevenue : undefined}
                            formatCurrencyValue={formatCurrencyValue}
                        />
                    )}

                    {/* Sales Achievement Tab Content */}
                    {activeTab === 'sales-achievement' && (
                        <SalesAchievement
                            salesData={salesData}
                            metrics={metrics}
                            isLoading={isLoading}
                            searchQuery={searchQuery}
                            setSearchQuery={setSearchQuery}
                            filters={filters}
                            handleExportExcel={can('export_data', 'sales-report') ? handleExportExcel : undefined}
                            formatCurrencyValue={formatCurrencyValue}
                        />
                    )}

                    {/* Sales by Service Item Tab Content */}
                    {activeTab === 'sales-by-service' && (
                        <SalesByServiceItem
                            salesByServiceData={salesByServiceData}
                            metrics={salesByServiceMetrics}
                            isLoading={isLoading}
                            searchQueryServiceItem={searchQueryServiceItem}
                            setSearchQueryServiceItem={setSearchQueryServiceItem}
                            handleExportExcelServiceItem={can('export_data', 'sales-report') ? handleExportExcelServiceItem : undefined}
                            formatCurrencyValue={formatCurrencyValue}
                        />
                    )}

                    {/* Customer Balance Tab Content */}
                    {activeTab === 'customer-balance' && (
                        <CustomerBalance
                            customerBalanceData={customerBalanceData}
                            salesListDetailData={salesListDetailData}
                            isLoading={isLoading}
                            searchQueryCustomerBalance={searchQueryCustomerBalance}
                            setSearchQueryCustomerBalance={setSearchQueryCustomerBalance}
                            handleExportExcelCustomerBalance={can('export_data', 'sales-report') ? handleExportExcelCustomerBalance : undefined}
                            formatCurrencyValue={formatCurrencyValue}
                            filters={customerBalanceFilters}
                        />
                    )}

                    {/* Sales List Detail Tab Content */}
                    {activeTab === 'sales-list-detail' && (
                        <SalesListDetail
                            salesListDetailData={salesListDetailData}
                            isLoading={isLoading}
                            searchQuerySalesListDetail={searchQuerySalesListDetail}
                            setSearchQuerySalesListDetail={setSearchQuerySalesListDetail}
                            handleExportExcelSalesListDetail={can('export_data', 'sales-report') ? handleExportExcelSalesListDetail : undefined}
                            formatCurrencyValue={formatCurrencyValue}
                            filters={salesListDetailFilters}
                        />
                    )}

                    {/* Contract Won Tab Content */}
                    {activeTab === 'total-contract-won' && (
                        <ContractWon
                            summary={totalRevenueSummary}
                            salesAchievementData={salesData}
                            achievementMetrics={metrics}
                            filters={filters}
                            searchQuery={searchQueryTotalContractWon}
                            setSearchQuery={setSearchQueryTotalContractWon}
                            isLoading={isLoading}
                            handleExportExcel={can('export_data', 'sales-report') ? handleExportExcelTotalRevenue : undefined}
                            formatCurrencyValue={formatCurrencyValue}
                        />
                    )}

                    {/* Other Tabs - Placeholder */}
                    {activeTab !== 'sales-achievement' && activeTab !== 'total-revenue' && activeTab !== 'sales-by-service' && activeTab !== 'customer-balance' && activeTab !== 'sales-list-detail' && activeTab !== 'total-contract-won' && (
                        <div className="card">
                            <div className="card-body text-center py-5">
                                <h4 className="text-muted">{activeTab.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} content coming soon</h4>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Filter Offcanvas */}
            <FilterOffcanvas
                activeTab={activeTab}
                filters={tempFilters}
                customerBalanceFilters={tempCustomerBalanceFilters}
                salesListDetailFilters={tempSalesListDetailFilters}
                salesByServiceFilters={tempSalesByServiceFilters}
                handleFilterChange={handleFilterChange}
                handleCustomerBalanceFilterChange={handleCustomerBalanceFilterChange}
                handleSalesListDetailFilterChange={handleSalesListDetailFilterChange}
                handleSalesByServiceFilterChange={handleSalesByServiceFilterChange}
                handleResetFilter={handleResetFilter}
                handleApplyFilter={handleApplyFilter}
                periodOptions={periodOptions}
                userOptions={userOptions}
                serviceOptions={serviceOptions}
                productCategoryOptions={productCategoryOptions}
                standardOptions={standardOptions}
                paymentOptions={paymentOptions}
                statusOptions={statusOptions}
                serviceItemOptions={serviceItemOptions}
                salesCreatorOptions={cascadedSalesCreatorOptions}
                cascadedUserOptions={cascadedSalesAchievementCreatorOptions}
                departmentOptions={departmentOptions}
                branchOptions={branchOptions}
                taggingOptions={taggingOptions}
                // Permission props
                canViewGlobal={can('view_global', 'sales-report')}
                canViewDepartment={can('view_department', 'sales-report')}
                canViewOwn={can('view_own', 'sales-report')}
                user={user}
            />

            {/* Custom Date Range Picker Modal */}
            {showCustomDatePicker && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Select Custom Date Range</h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={handleCancelCustomDateRange}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label htmlFor="startDate" className="form-label">Start Date</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            id="startDate"
                                            value={customDateRange.startDate}
                                            onChange={(e) => handleCustomDateChange('startDate', e.target.value)}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label htmlFor="endDate" className="form-label">End Date</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            id="endDate"
                                            value={customDateRange.endDate}
                                            onChange={(e) => handleCustomDateChange('endDate', e.target.value)}
                                            min={customDateRange.startDate}
                                        />
                                    </div>
                                </div>
                                {dateRangeError && (
                                    <div className="mt-3">
                                        <div className="alert alert-danger" role="alert">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon alert-icon">
                                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71 -3L13.71 3.86a2 2 0 0 0 -3.42 0z"></path>
                                                <line x1="12" y1="9" x2="12" y2="13"></line>
                                                <line x1="12" y1="17" x2="12.01" y2="17"></line>
                                            </svg>
                                            {dateRangeError}
                                        </div>
                                    </div>
                                )}
                                {customDateRange.startDate && customDateRange.endDate && !dateRangeError && (
                                    <div className="mt-3">
                                        <small className="text-muted">
                                            Selected range: {new Date(customDateRange.startDate).toLocaleDateString()} - {new Date(customDateRange.endDate).toLocaleDateString()}
                                        </small>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={handleCancelCustomDateRange}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-primary" 
                                    onClick={handleApplyCustomDateRange}
                                    disabled={!customDateRange.startDate || !customDateRange.endDate || dateRangeError}
                                >
                                    Apply Range
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SalesReport;
